import type { DashboardMetric, DashboardMetricBreakdownEntry } from '../../types/dashboard';

function slugifySubawardee(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_|_$/g, '');
}

function resolveSubawardeeIdFromKey(key: string, subawardeeIds: string[]): string | null {
  const normalizedKey = key.trim().toLowerCase();
  const slugKey = slugifySubawardee(key);

  for (const subawardeeId of subawardeeIds) {
    if (subawardeeId.toLowerCase() === normalizedKey) return subawardeeId;
    if (slugifySubawardee(subawardeeId) === slugKey) return subawardeeId;
  }

  return null;
}

export function readNullableNumericField(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  return 0;
}

function resolveBreakdownSubawardeeId(
  rawKey: string,
  entry: { groupId?: string; groupName?: string },
  subawardeeIds: string[]
): string {
  return (
    resolveSubawardeeIdFromKey(rawKey, subawardeeIds) ??
    resolveSubawardeeIdFromKey(entry.groupName ?? '', subawardeeIds) ??
    resolveSubawardeeIdFromKey(entry.groupId ?? '', subawardeeIds) ??
    rawKey
  );
}

export function readSubawardeeFieldBreakdown(
  metric: DashboardMetric,
  subawardeeIds: string[],
  fieldKeys: string[]
): Map<string, Record<string, number>> | null {
  const bySubawardee = metric.breakdowns?.bySubawardee;
  if (!bySubawardee) return null;

  const byQuarter = new Map<string, Record<string, number>>();

  for (const [rawKey, entry] of Object.entries(bySubawardee)) {
    const breakdownEntry = entry as DashboardMetricBreakdownEntry;
    const subawardeeId = resolveBreakdownSubawardeeId(rawKey, breakdownEntry, subawardeeIds);

    for (const point of breakdownEntry.series ?? []) {
      const quarter = String(point.quarter ?? '');
      if (!quarter) continue;

      let value = 0;
      for (const fieldKey of fieldKeys) {
        if (fieldKey in point) {
          value = readNullableNumericField(point[fieldKey]);
          break;
        }
      }

      const merged = byQuarter.get(quarter) ?? {};
      merged[subawardeeId] = value;
      byQuarter.set(quarter, merged);
    }
  }

  return byQuarter.size > 0 ? byQuarter : null;
}

export function collectActiveSubawardeeIds(
  byQuarter: Map<string, Record<string, number>>,
  configuredSubawardeeIds: string[]
): string[] {
  const active = new Set<string>();

  for (const values of Array.from(byQuarter.values())) {
    for (const [subawardeeId, value] of Object.entries(values)) {
      if (typeof value === 'number' && value > 0) {
        active.add(subawardeeId);
      }
    }
  }

  return configuredSubawardeeIds.filter((subawardeeId) => active.has(subawardeeId));
}
